/**
 * useStandardView.tsx - useStandardView Hook (v2 - TypeScript with Interface Enforcement)
 * 
 * CHANGE HISTORY:
 * - 251020_0628 [1a99944] danielbeer: feat: migrate useStandardView to TypeScript with interface enforcement
 */
import { useCallback, useMemo } from 'react';
import { useActr } from '@opcentral/actr';
import type { IDataOperations } from '@opcentral/actr';
import { useBacktrackr } from '@opcentral/backtrackr';
import { useClipr } from '@opcentral/clippr';
import { useCommandr } from '@opcentral/commandr';
import { useSkinnr } from '@opcentral/skinnr';
import { useSearchr } from '@opcentral/searchr';

/**
 * Configuration options for useStandardView
 */
export interface UseStandardViewConfig {
  /** Enable clipboard operations (copy/cut/paste) */
  enableClipboard?: boolean;
  /** Enable search functionality */
  enableSearch?: boolean;
}

/**
 * Return type for useStandardView hook
 */
export interface StandardViewOperations<T = any> {
  // CRUD operations (delegated to Actr with automatic middleware)
  query: (filters?: Record<string, any>) => Promise<T[]>;
  create: (data: Partial<T>) => Promise<T>;
  read: (id: string) => Promise<T | null>;
  update: (id: string, updates: Partial<T>) => Promise<T>;
  delete: (id: string) => Promise<boolean>;
  
  // Clipboard operations (view-specific)
  copy: (items: T | T[]) => void;
  cut: (items: T | T[]) => void;
  paste: () => Promise<T[]>;
  canPaste: () => boolean;
  
  // Undo/redo (direct controls)
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  
  // Search (view-specific)
  search: (query: string, options?: Record<string, any>) => Promise<T[]>;
  
  // Theming (view-specific)
  getColor: (name: string) => string;
  getSpacing: (size: string) => string;
  theme: any;
  
  // Direct module access (for advanced usage)
  modules: {
    actr: IDataOperations;
    backtrackr: any;
    clippr: any;
    commandr: any;
    skinnr: any;
    searchr: any;
  };
  
  // View metadata
  viewId: string;
  viewType: string;
  config: UseStandardViewConfig;
}

/**
 * Standard view hook (v2 - TypeScript with Interface Enforcement)
 * 
 * Actr handles automatically (via middleware):
 * - ✅ Permission checks (Permittr)
 * - ✅ Logging (Loggr)
 * - ✅ Notifications (Notifyr)
 * - ✅ Undo recording (Backtrackr)
 * 
 * This hook provides:
 * - Clipboard operations (view-specific)
 * - Search (view-specific)
 * - Theming (view-specific)
 * - Direct undo/redo controls
 * 
 * @param viewId - Unique identifier for this view instance
 * @param viewType - Type of view (e.g., 'kanban', 'outliner', 'spreadsheet')
 * @param config - View configuration options
 * @returns Standard operations and utilities
 * 
 * @example
 * ```typescript
 * const operations = useStandardView<Task>('my-kanban-1', 'task');
 * 
 * // CRUD operations (automatic permissions, logging, notifications, undo)
 * const items = await operations.query({ status: 'active' });
 * const item = await operations.create({ title: 'New Task' });
 * await operations.update(itemId, { status: 'done' });
 * await operations.delete(itemId);
 * 
 * // Clipboard (view-specific)
 * operations.copy(selectedItems);
 * const pasted = await operations.paste();
 * 
 * // Undo/redo (direct controls)
 * operations.undo();
 * operations.redo();
 * ```
 */
export const useStandardView = <T = any>(
  viewId: string,
  viewType: string,
  config: UseStandardViewConfig = {}
): StandardViewOperations<T> => {
  // Core data operations (with automatic middleware)
  // actr implements IDataOperations - enforced by TypeScript!
  const actr: IDataOperations = useActr();
  
  // View-specific modules
  // TODO: Add proper TypeScript definitions for these hooks
  const backtrackr = useBacktrackr() as any;
  const clippr = useClipr() as any;
  const commandr = useCommandr() as any;
  const skinnr = useSkinnr() as any;
  const searchr = useSearchr() as any;
  
  // Extract config options
  const {
    enableClipboard = true,
    enableSearch = true
  } = config;
  
  // ========================================
  // CRUD OPERATIONS (Delegated to Actr)
  // ========================================
  // Actr automatically handles:
  // - Permission checks
  // - Logging
  // - Notifications
  // - Undo recording
  
  /**
   * Query items with filters
   * Actr handles: permissions, logging, notifications
   */
  const query = useCallback(async (filters: Record<string, any> = {}): Promise<T[]> => {
    return await actr.query<T>(viewType, { viewId, ...filters });
  }, [actr, viewId, viewType]);
  
  /**
   * Create a new item
   * Actr handles: permissions, logging, notifications, undo recording
   */
  const create = useCallback(async (data: Partial<T>): Promise<T> => {
    return await actr.create<T>(viewType, data);
  }, [actr, viewType]);
  
  /**
   * Read a single item
   * Actr handles: logging
   */
  const read = useCallback(async (id: string): Promise<T | null> => {
    return await actr.read<T>(viewType, id);
  }, [actr, viewType]);
  
  /**
   * Update an existing item
   * Actr handles: permissions, logging, notifications, undo recording
   */
  const update = useCallback(async (id: string, updates: Partial<T>): Promise<T> => {
    return await actr.update<T>(viewType, id, updates);
  }, [actr, viewType]);
  
  /**
   * Delete an item
   * Actr handles: permissions, logging, notifications, undo recording
   */
  const deleteItem = useCallback(async (id: string): Promise<boolean> => {
    return await actr.delete(viewType, id);
  }, [actr, viewType]);
  
  // ========================================
  // CLIPBOARD OPERATIONS (View-Specific)
  // ========================================
  
  /**
   * Copy items to clipboard
   * View-specific: decides what to copy and when
   */
  const copy = useCallback((items: T | T[]): void => {
    if (!enableClipboard) return;
    
    const itemsArray = Array.isArray(items) ? items : [items];
    clippr.copy(itemsArray, { 
      metadata: { viewId, viewType, timestamp: Date.now() } 
    });
  }, [clippr, viewId, viewType, enableClipboard]);
  
  /**
   * Cut items to clipboard
   * View-specific: decides what to cut and when
   */
  const cut = useCallback((items: T | T[]): void => {
    if (!enableClipboard) return;
    
    const itemsArray = Array.isArray(items) ? items : [items];
    clippr.cut(itemsArray, { 
      metadata: { viewId, viewType, timestamp: Date.now() } 
    });
  }, [clippr, viewId, viewType, enableClipboard]);
  
  /**
   * Paste items from clipboard
   * View-specific: decides how to paste into this view
   * Note: create() calls will trigger Actr middleware (permissions, notifications, undo)
   */
  const paste = useCallback(async (): Promise<T[]> => {
    if (!enableClipboard || !clippr.hasData()) {
      return [];
    }
    
    const clipboardData = clippr.paste();
    const itemsArray = Array.isArray(clipboardData) ? clipboardData : [clipboardData];
    
    // Create items in current view (Actr handles permissions, notifications, undo)
    const created = await Promise.all(
      itemsArray.map((item: Partial<T>) => create(item))
    );
    
    return created.filter(Boolean) as T[];
  }, [clippr, create, enableClipboard]);
  
  /**
   * Check if clipboard has data
   */
  const canPaste = useCallback((): boolean => {
    return enableClipboard && clippr.hasData();
  }, [clippr, enableClipboard]);
  
  // ========================================
  // UNDO/REDO (Direct Controls)
  // ========================================
  // Note: Recording is automatic via Actr middleware
  // This just provides direct undo/redo controls
  
  const undo = useCallback((): void => {
    backtrackr.undo();
  }, [backtrackr]);
  
  const redo = useCallback((): void => {
    backtrackr.redo();
  }, [backtrackr]);
  
  const canUndo = useCallback((): boolean => {
    return backtrackr.canUndo();
  }, [backtrackr]);
  
  const canRedo = useCallback((): boolean => {
    return backtrackr.canRedo();
  }, [backtrackr]);
  
  // ========================================
  // SEARCH (View-Specific)
  // ========================================
  
  /**
   * Search within view
   * View-specific: defines search scope
   */
  const search = useCallback(async (
    query: string, 
    options: Record<string, any> = {}
  ): Promise<T[]> => {
    if (!enableSearch) return [];
    
    return await searchr.search(query, { 
      viewId, 
      viewType,
      ...options 
    });
  }, [searchr, viewId, viewType, enableSearch]);
  
  // ========================================
  // THEMING (View-Specific)
  // ========================================
  
  /**
   * Get theme color
   */
  const getColor = useCallback((name: string): string => {
    return skinnr.getColor(name);
  }, [skinnr]);
  
  /**
   * Get theme spacing
   */
  const getSpacing = useCallback((size: string): string => {
    return skinnr.getSpacing(size);
  }, [skinnr]);
  
  // ========================================
  // RETURN API
  // ========================================
  
  return useMemo(() => ({
    // CRUD operations (Actr handles middleware automatically)
    query,
    create,
    read,
    update,
    delete: deleteItem,
    
    // Clipboard operations (view-specific)
    copy,
    cut,
    paste,
    canPaste,
    
    // Undo/redo (direct controls, recording is automatic)
    undo,
    redo,
    canUndo,
    canRedo,
    
    // Search (view-specific)
    search,
    
    // Theming (view-specific)
    getColor,
    getSpacing,
    theme: skinnr.theme,
    
    // Direct module access (for advanced usage)
    modules: {
      actr,
      backtrackr,
      clippr,
      commandr,
      skinnr,
      searchr
    },
    
    // View metadata
    viewId,
    viewType,
    config
  }), [
    query, create, read, update, deleteItem,
    copy, cut, paste, canPaste,
    undo, redo, canUndo, canRedo,
    search,
    getColor, getSpacing, skinnr.theme,
    actr, backtrackr, clippr, commandr, skinnr, searchr,
    viewId, viewType, config
  ]);
};

export default useStandardView;
